import { AUDIO_CONFIG } from './constants';

export const createControlHandlers = (
  audioState, 
  audioRef, 
  canvasRef,
  loadStreamingAudio, 
  drawTimeline, 
  formatTimepoint,
  onCardFocusChange
) => {
  // Debouncing for timeline seeking
  let seekTimeoutId = null;
  let lastSeekTime = 0;
  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (audioState.isLoading) return;

    try {
      if (!audioState.hasStreamInitiated) {
        console.log('🎵 Initiating streaming on first play...');
        audioState.setHasStreamInitiated(true);
        const startTime = audioState.currentTime || 0;
        loadStreamingAudio(Math.floor(startTime), true);
        
        // Trigger card focus change to scroll to the appropriate card when starting playback
        if (onCardFocusChange) {
          onCardFocusChange(startTime);
        }
        return;
      }

      if (!audio) {
        console.log('🎵 Audio not ready, initiating streaming...');
        const startTime = audioState.currentTime || 0;
        loadStreamingAudio(Math.floor(startTime), true);
        
        // Trigger card focus change to scroll to the appropriate card when starting playback
        if (onCardFocusChange) {
          onCardFocusChange(startTime);
        }
        return;
      }

      if (audioState.isPlaying) {
        audio.pause();
      } else {
        audio.play().catch(e => {
          console.warn('Play failed:', e);
          audioState.setError('Playback failed - please try again');
        });
      }
    } catch (error) {
      console.error('Play/pause error:', error);
      audioState.setError('Playback error: ' + (error.message || 'Unknown error'));
    }
  };

  const handleVolumeChange = (_, newValue) => {
    audioState.setVolume(newValue);
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newValue;
      const shouldBeMuted = newValue === 0;
      audio.muted = shouldBeMuted;
      audioState.setIsMuted(shouldBeMuted);
    }
  };

  const handleMuteToggle = () => {
    const newMutedState = !audioState.isMuted;
    audioState.setIsMuted(newMutedState);
    const audio = audioRef.current;
    if (audio) {
      audio.muted = newMutedState;
    }
  };

  const handleSkipBackward = () => {
    const newTime = Math.max(0, audioState.currentTime - AUDIO_CONFIG.SKIP_SECONDS);
    console.log('⏪ Skipping backward 5s to:', formatTimepoint(newTime));
    audioState.setCurrentTime(newTime);
    drawTimeline();
    audioState.setHasStreamInitiated(true);
    loadStreamingAudio(Math.floor(newTime), audioState.isPlaying);
    
    // Trigger card focus change to scroll to the appropriate card
    if (onCardFocusChange) {
      onCardFocusChange(newTime);
    }
  };

  const handleSkipForward = () => {
    const maxTime = audioState.duration || 0;
    const newTime = Math.min(maxTime, audioState.currentTime + AUDIO_CONFIG.SKIP_SECONDS);
    console.log('⏩ Skipping forward 5s to:', formatTimepoint(newTime));
    audioState.setCurrentTime(newTime);
    drawTimeline();
    audioState.setHasStreamInitiated(true);
    loadStreamingAudio(Math.floor(newTime), audioState.isPlaying);
    
    // Trigger card focus change to scroll to the appropriate card
    if (onCardFocusChange) {
      onCardFocusChange(newTime);
    }
  };

  const handleSpeedToggle = () => {
    const currentIndex = AUDIO_CONFIG.PLAYBACK_SPEEDS.indexOf(audioState.playbackRate);
    const nextIndex = (currentIndex + 1) % AUDIO_CONFIG.PLAYBACK_SPEEDS.length;
    const newSpeed = AUDIO_CONFIG.PLAYBACK_SPEEDS[nextIndex];
    
    console.log('🚀 Changing playback speed to:', newSpeed + 'x');
    audioState.setPlaybackRate(newSpeed);
    
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = newSpeed;
    }
  };

  const handleTimelineClick = (event) => {
    const canvas = canvasRef.current;
    if (!canvas || !audioState.duration) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const progress = clickX / rect.width;
    const seekTime = progress * audioState.duration;

    console.log('🎯 Timeline click - seeking to:', formatTimepoint(seekTime));

    // Immediate visual feedback
    audioState.setCurrentTime(seekTime);
    drawTimeline();

    // Clear any pending seek request
    if (seekTimeoutId) {
      clearTimeout(seekTimeoutId);
    }

    // Debounce the actual seeking to prevent rapid requests
    const now = Date.now();
    const timeSinceLastSeek = now - lastSeekTime;

    if (timeSinceLastSeek < AUDIO_CONFIG.SEEK_DEBOUNCE_MS) {
      // If user is rapidly clicking, delay the actual seek
      seekTimeoutId = setTimeout(() => {
        performSeek(seekTime);
      }, AUDIO_CONFIG.SEEK_DELAY_MS);
    } else {
      // Immediate seek for single clicks
      performSeek(seekTime);
    }

    lastSeekTime = now;
  };

  const performSeek = (seekTime) => {
    console.log('🎯 Performing seek to:', formatTimepoint(seekTime));
    audioState.setHasStreamInitiated(true);
    loadStreamingAudio(Math.floor(seekTime), audioState.isPlaying);
    
    // Trigger card focus change to scroll to the appropriate card
    if (onCardFocusChange) {
      onCardFocusChange(seekTime);
    }
  };

  // Direct seek function for programmatic navigation (like card navigation)
  const seekToTime = (seekTime) => {
    console.log('🎯 Direct seek to:', formatTimepoint(seekTime));
    
    // Immediate visual feedback
    audioState.setCurrentTime(seekTime);
    drawTimeline();
    
    // Perform the seek
    performSeek(seekTime);
  };

  const handleReload = () => {
    console.log('🔄 Reloading audio stream from current position...');
    audioState.setError(null);
    audioState.setHasStreamInitiated(true);
    loadStreamingAudio(Math.floor(audioState.currentTime || 0), audioState.isPlaying);
  };

  return {
    handlePlayPause,
    handleVolumeChange,
    handleMuteToggle,
    handleSkipBackward,
    handleSkipForward,
    handleSpeedToggle,
    handleTimelineClick,
    handleReload,
    seekToTime
  };
};
